import dayjs from "dayjs";

import {
    TextCell,
    shortMonthDayAndYear,
    shortMonthAndYearWithDelimiter,
    TextAnchorCell,
} from "@similarweb/pro-logic-components";
import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import { hasAdIntelligenceOverviewAccess, isDailyGranularityLocked } from "../../../common/utils/utils";
import type { DisplayAdvertiserPublisherParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";

export const getColumns = (
    params: DisplayAdvertiserPublisherParams,
    { isPreview = false }: { isPreview?: boolean } = {},
): ColumnsType<AnyObject> =>
    [
        {
            title: () => (
                <TextCell
                    fontVariant="captionTitle"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("ad.intelligence.advertiser.top.campaigns.campaigns.column.title")}
                </TextCell>
            ),
            dataIndex: "campaignName",
            render: (name) => <TextCell>{name}</TextCell>,
        },
        {
            title: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.title"),
            dataIndex: "landingPageLink",
            render: (link) => {
                const formattedLink = /^https?:\/\//.test(link) ? link : `https://${link}`;

                return (
                    <TextAnchorCell
                        target="_blank"
                        href={formattedLink}
                        onClick={() => {
                            allTrackers.dispatchTrackEvent("External Link", "click", `Table/URL/${link}`);
                        }}
                    >
                        {link}
                    </TextAnchorCell>
                );
            },
        },
        {
            title: translate("ad.intelligence.advertiser.campaigns.creatives.column.title"),
            dataIndex: "creativesCount",
            width: 70,
            align: "right",
            render: (CreativesCount, row) => {
                const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
                const formattedCount = abbrNumberFilter()(CreativesCount);

                if (!hasAdIntelBeta) {
                    return <TextCell>{formattedCount}</TextCell>;
                }

                return (
                    <Tooltip content={translate("ad.intelligence.advertiser.campaigns.creatives.cell.tooltip.title")}>
                        <span>
                            <TextAnchorCell
                                onClick={() =>
                                    void displayAdvertisingNavigation.ads.go({
                                        ...params,
                                        selectedCampaigns: [row.campaignId],
                                        adsSelectedSite: params.campaignsSelectedSite,
                                        ignoreFilterConsistency: true,
                                    })
                                }
                            >
                                {formattedCount}
                            </TextAnchorCell>
                        </span>
                    </Tooltip>
                );
            },
        },
        ...(!isPreview && !isDailyGranularityLocked()
            ? [
                  {
                      title: translate("ad.intelligence.advertiser.campaigns.active.days.column.title"),
                      dataIndex: "activeDays",
                      width: 90,
                      align: "left" as const,
                      render: (ActiveDays: number) =>
                          `${numberFilter()(ActiveDays)} ${translate(
                              ActiveDays > 1
                                  ? "ad.intelligence.advertiser.campaigns.active.days.column.days"
                                  : "ad.intelligence.advertiser.campaigns.active.days.column.day",
                          )}`,
                  },
              ]
            : []),
        {
            title: translate("ad.intelligence.advertiser.campaigns.first.seen.column.title"),
            dataIndex: "firstSeen",
            width: 120,
            align: "left",
            render: (FirstSeen) =>
                dayjs(FirstSeen).format(
                    isPreview || isDailyGranularityLocked() ? shortMonthAndYearWithDelimiter : shortMonthDayAndYear,
                ),
        },
        {
            title: translate("ad.intelligence.advertiser.campaigns.last.seen.column.title"),
            dataIndex: "lastSeen",
            width: 120,
            align: "left",
            render: (LastSeen) =>
                dayjs(LastSeen).format(
                    isPreview || isDailyGranularityLocked() ? shortMonthAndYearWithDelimiter : shortMonthDayAndYear,
                ),
        },
    ] as ColumnsType<AnyObject>;
